import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { APP_VERSION, getDeployTarget } from '../config/environment';

export const CHANGELOG_DOC_ID = 'changelog';

export const CHANGELOG_AUDIENCE_ALL = 'all';
export const CHANGELOG_AUDIENCE_ADMIN = 'admin';

export const CHANGELOG_DEPLOY_BOTH = 'both';
export const CHANGELOG_DEPLOY_PRODUCTION = 'production';
export const CHANGELOG_DEPLOY_STAGING = 'staging';

const SEEN_STORAGE_PREFIX = 'vrc-whats-new-seen';

export function normalizeChangelogAudience(raw) {
  return raw === CHANGELOG_AUDIENCE_ADMIN ? CHANGELOG_AUDIENCE_ADMIN : CHANGELOG_AUDIENCE_ALL;
}

export function normalizeChangelogDeployTarget(raw) {
  if (raw === CHANGELOG_DEPLOY_PRODUCTION || raw === CHANGELOG_DEPLOY_STAGING) return raw;
  return CHANGELOG_DEPLOY_BOTH;
}

/** Maps localhost/development to staging for release matching. */
export function resolveChangelogDeployContext(deployTarget = getDeployTarget()) {
  if (deployTarget === CHANGELOG_DEPLOY_PRODUCTION) return CHANGELOG_DEPLOY_PRODUCTION;
  return CHANGELOG_DEPLOY_STAGING;
}

export function releaseDeployTargetMatches(releaseDeployTarget, deployContext) {
  const release = normalizeChangelogDeployTarget(releaseDeployTarget);
  if (release === CHANGELOG_DEPLOY_BOTH) return true;
  return release === resolveChangelogDeployContext(deployContext);
}

export function buildReleaseId({ version, audience, deployTarget }) {
  let id = String(version || '').trim();
  if (!id) return '';
  if (normalizeChangelogAudience(audience) === CHANGELOG_AUDIENCE_ADMIN) id += '__admin';
  const deploy = normalizeChangelogDeployTarget(deployTarget);
  if (deploy !== CHANGELOG_DEPLOY_BOTH) id += `__${deploy}`;
  return id;
}

export function releaseSeenKey(release) {
  const version = String(release?.version || release?.id || '').trim();
  if (!version) return '';
  return buildReleaseId({
    version,
    audience: release?.audience,
    deployTarget: release?.deployTarget,
  });
}

export function releasesMatch(a, b) {
  if (!a || !b) return false;
  if (a.id && b.id && a.id === b.id) return true;
  return (
    String(a.version || '') === String(b.version || '') &&
    normalizeChangelogAudience(a.audience) === normalizeChangelogAudience(b.audience) &&
    normalizeChangelogDeployTarget(a.deployTarget) === normalizeChangelogDeployTarget(b.deployTarget)
  );
}

export function cardShowsCta(card) {
  if (card?.showCta === false) return false;
  const route = String(card?.ctaRoute || '').trim();
  const label = String(card?.ctaLabel || '').trim();
  return Boolean(route && label);
}

function generateCardId() {
  return `card_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function normalizeChangelogCard(raw = {}) {
  const ctaRoute = String(raw.ctaRoute || '').trim();
  const ctaLabel = String(raw.ctaLabel || '').trim();
  return {
    id: String(raw.id || generateCardId()),
    icon: String(raw.icon || 'sparkles-outline').trim() || 'sparkles-outline',
    title: String(raw.title || '').trim(),
    body: String(raw.body || '').trim(),
    showCta: raw.showCta !== false,
    ctaLabel,
    ctaRoute,
    ctaParams: raw.ctaParams && typeof raw.ctaParams === 'object' ? raw.ctaParams : {},
  };
}

export function normalizeChangelogRelease(raw = {}) {
  const version = String(raw.version || '').trim();
  const audience = normalizeChangelogAudience(raw.audience);
  const deployTarget = normalizeChangelogDeployTarget(raw.deployTarget);
  const cards = (Array.isArray(raw.cards) ? raw.cards : [])
    .map(normalizeChangelogCard)
    .filter((c) => c.title || c.body);
  return {
    id: String(raw.id || buildReleaseId({ version, audience, deployTarget }) || generateCardId()),
    version,
    audience,
    deployTarget,
    title: String(raw.title || '').trim() || (version ? `What's new in v${version}` : "What's new"),
    subtitle: String(raw.subtitle || '').trim(),
    published: raw.published !== false,
    cards,
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  };
}

export function normalizeChangelogConfig(data = {}) {
  return {
    enabled: data.enabled !== false,
    linkToLatest: data.linkToLatest !== false,
    pinnedVersion: String(data.pinnedVersion || '').trim(),
    releases: (Array.isArray(data.releases) ? data.releases : [])
      .map(normalizeChangelogRelease)
      .filter((r) => r.version)
      .sort((a, b) => compareVersions(b.version, a.version)),
    updatedAt: data.updatedAt || null,
    updatedBy: data.updatedBy || null,
  };
}

export function compareVersions(a, b) {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function findPublishedReleaseForVersion(published, version, audience, deployContext) {
  return published.find(
    (r) =>
      r.version === version &&
      normalizeChangelogAudience(r.audience) === audience &&
      releaseDeployTargetMatches(r.deployTarget, deployContext)
  );
}

export function resolveActiveReleases(
  config,
  appVersion = APP_VERSION,
  { isAdmin = false, deployTarget = getDeployTarget() } = {}
) {
  const normalized = normalizeChangelogConfig(config);
  if (!normalized.enabled || !normalized.releases.length) return [];

  const published = normalized.releases.filter((r) => r.published && r.cards.length);
  if (!published.length) return [];

  const targetVersion = normalized.linkToLatest
    ? appVersion
    : normalized.pinnedVersion || appVersion;

  const deployContext = resolveChangelogDeployContext(deployTarget);
  const releases = [];
  if (isAdmin) {
    const adminRelease = findPublishedReleaseForVersion(
      published,
      targetVersion,
      CHANGELOG_AUDIENCE_ADMIN,
      deployContext
    );
    const publicRelease = findPublishedReleaseForVersion(
      published,
      targetVersion,
      CHANGELOG_AUDIENCE_ALL,
      deployContext
    );
    if (adminRelease) releases.push(adminRelease);
    if (publicRelease) releases.push(publicRelease);
    return releases;
  }

  const publicRelease = findPublishedReleaseForVersion(
    published,
    targetVersion,
    CHANGELOG_AUDIENCE_ALL,
    deployContext
  );
  return publicRelease ? [publicRelease] : [];
}

export function resolveActiveRelease(config, appVersion = APP_VERSION, options = {}) {
  return resolveActiveReleases(config, appVersion, options)[0] || null;
}

export function subscribeChangelogConfig(db, callback) {
  if (!db) {
    callback(normalizeChangelogConfig());
    return () => {};
  }
  const ref = doc(db, 'system', CHANGELOG_DOC_ID);
  return onSnapshot(
    ref,
    (snap) => {
      callback(normalizeChangelogConfig(snap.exists() ? snap.data() : {}));
    },
    () => callback(normalizeChangelogConfig())
  );
}

export async function saveChangelogConfig(db, config, userId) {
  const normalized = normalizeChangelogConfig(config);
  const ref = doc(db, 'system', CHANGELOG_DOC_ID);
  await setDoc(
    ref,
    {
      ...normalized,
      updatedAt: serverTimestamp(),
      updatedBy: userId || null,
    },
    { merge: true }
  );
  return normalized;
}

export function hasSeenRelease(release, { userId, userSeenMap = {} } = {}) {
  const key = releaseSeenKey(release);
  if (!key) return true;
  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage.getItem(`${SEEN_STORAGE_PREFIX}-${key}`) === '1') return true;
    } catch {
      /* ignore */
    }
  }
  if (userId && userSeenMap?.[key]) return true;
  return false;
}

export async function markReleaseSeen(db, release, userId) {
  const key = releaseSeenKey(release);
  if (!key) return;

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(`${SEEN_STORAGE_PREFIX}-${key}`, '1');
    } catch {
      /* ignore */
    }
  }

  if (userId && db) {
    await updateDoc(doc(db, 'users', userId), {
      [`whatsNewSeen.${key}`]: serverTimestamp(),
      lastSeenWhatsNewVersion: key,
    }).catch(async () => {
      await setDoc(
        doc(db, 'users', userId),
        {
          whatsNewSeen: { [key]: serverTimestamp() },
          lastSeenWhatsNewVersion: key,
        },
        { merge: true }
      );
    });
  }
}

export function buildDefaultRelease(version = APP_VERSION || '1.5.6') {
  return normalizeChangelogRelease({
    id: version,
    version,
    title: `What's new in v${version}`,
    subtitle: 'Tour calendar sharing, reminders, and a smoother day-by-day workflow.',
    published: true,
    cards: [
      {
        id: 'tour-settings',
        icon: 'settings-outline',
        title: 'Tour settings & scheduled sends',
        body:
          'Set global reminder defaults for your tour, then override any show day individually. Get notified by email and in-app before riders and share links go out.',
        ctaLabel: 'Open calendar',
        ctaRoute: 'MyCalendar',
      },
      {
        id: 'shared-links',
        icon: 'link-outline',
        title: 'Shared links manager',
        body:
          'Manage every day share link in one place — reorder sections, invite viewers, email links, and see scheduled send times on each card.',
        ctaLabel: 'Manage shared links',
        ctaRoute: 'MyCalendar',
      },
      {
        id: 'day-schedule',
        icon: 'notifications-outline',
        title: 'Per-day schedule controls',
        body:
          'Under each date in the sidebar, see whether scheduled sends are on, using tour defaults or a custom exception for that show day.',
        ctaLabel: 'View a show day',
        ctaRoute: 'MyCalendar',
      },
      {
        id: 'stage-rider',
        icon: 'document-text-outline',
        title: 'Stage & rider improvements',
        body:
          'Stage files show real thumbnails, copy-to scopes match the rider tab, and sidebar tabs make it easy to jump between sections on a day.',
        ctaLabel: 'Go to calendar',
        ctaRoute: 'MyCalendar',
      },
      {
        id: 'calendar-nav',
        icon: 'calendar-outline',
        title: 'Smarter calendar navigation',
        body:
          'Prev/next day chevrons, tab memory when switching tours, and at-a-glance indicators on days with scheduled sends.',
        ctaLabel: 'Explore calendar',
        ctaRoute: 'MyCalendar',
      },
    ],
  });
}

export async function ensureDefaultChangelog(db, userId) {
  const ref = doc(db, 'system', CHANGELOG_DOC_ID);
  const snap = await getDoc(ref);
  if (snap.exists() && (snap.data()?.releases || []).length) {
    return normalizeChangelogConfig(snap.data());
  }
  const version = APP_VERSION || '1.5.6';
  const config = normalizeChangelogConfig({
    enabled: true,
    linkToLatest: true,
    pinnedVersion: version,
    releases: [buildDefaultRelease(version)],
  });
  await saveChangelogConfig(db, config, userId);
  return config;
}
